import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus, Trash2, X, Upload, ExternalLink, CheckCircle2, XCircle,
  Link2, ImageOff, Loader2, Pencil,
} from "lucide-react";
import adminApi, { STATIC_ORIGIN } from "./adminApi";

// const SEQUENCE_OPTIONS = Array.from({ length: 11 }, (_, i) => i + 1);

const emptyForm = { link_name: "", link_url: "", seq: 1 };

// function logoUrl(filename) {
//   if (!filename) return null;
//   return `${STATIC_ORIGIN}/images/logoimage/${filename}`;
// }

function logoUrl(filename) {
  if (!filename) return null;
  const fileName = filename.split(/[\\/]/).pop();

  return `${STATIC_ORIGIN}/images/logoimage/${fileName}`;
}

export default function ManageLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = "add new"
  const [form, setForm] = useState(emptyForm);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [existingLogo, setExistingLogo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const fileInputRef = useRef(null);

  const [toast, setToast] = useState(null); // { type: 'success'|'error', message }

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2600);
  };

  const fetchLinks = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.get("/admin/links");
      setLinks(res.data.links || []);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load links.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, []);

  // ── Selection ────────────────────────────────────────────────────────────
  const toggleSelectAll = () => {
    setSelected(selected.length === links.length ? [] : links.map((l) => l.linkid));
  };
  const toggleSelectOne = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // ── Status toggle ────────────────────────────────────────────────────────
  const handleToggleStatus = async (link, e) => {
    e.stopPropagation();
    const turningOn = link.status === 0;
    const ok = window.confirm(
      `Are you sure you want to change the status to ${turningOn ? "Active" : "Inactive"}?`
    );
    if (!ok) return;

    try {
      const res = await adminApi.patch(`/admin/links/${link.linkid}/status`);
      setLinks((prev) =>
        prev.map((l) => (l.linkid === link.linkid ? { ...l, status: res.data.status } : l))
      );
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not update status.");
    }
  };

  // ── Bulk delete ──────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    const ok = window.confirm(
      `Are you sure you want to delete ${selected.length} selected link(s)? This cannot be undone.`
    );
    if (!ok) return;

    setBulkDeleting(true);
    try {
      await adminApi.post("/admin/links/bulk-delete", { ids: selected });
      showToast("success", `${selected.length} link(s) deleted.`);
      setSelected([]);
      fetchLinks();
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not delete selected links.");
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── Add / Edit modal ─────────────────────────────────────────────────────
  // const openAddModal = () => {
  //   setEditingId(null);
  //   setForm(emptyForm);
  //   setLogoFile(null);
  //   setLogoPreview(null);
  //   setExistingLogo(null);
  //   setFormError("");
  //   setShowModal(true);
  // };

    const openAddModal = () => {
    setEditingId(null);

    // Find the highest existing sequence and add 1
    const maxSequence = links.reduce((max, link) => {
      const seq = Number(link.seq) || 0;
      return Math.max(max, seq);
    }, 0);

    const nextSequence = maxSequence + 1;

    setForm({
      link_name: "",
      link_url: "",
      seq: nextSequence,
    });

    setLogoFile(null);
    setLogoPreview(null);
    setExistingLogo(null);
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (link) => {
    setEditingId(link.linkid);
    setForm({ link_name: link.link_name, link_url: link.link_url, seq: link.seq });
    setLogoFile(null);
    setLogoPreview(null);
    setExistingLogo(link.link_logo);
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
    if (![".jpg", ".jpeg", ".png"].includes(ext)) {
      setFormError("Only JPG, JPEG and PNG files are allowed.");
      return;
    }
    setFormError("");
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.link_name.trim() || !form.link_url.trim() || !form.seq) {
      setFormError("Link Name, Website URL and Sequence are required.");
      return;
    }
    if (!editingId && !logoFile) {
      setFormError("Please choose a logo image.");
      return;
    }

    const fd = new FormData();
    fd.append("link_name", form.link_name.trim());
    fd.append("link_url", form.link_url.trim());
    fd.append("seq", form.seq);
    if (logoFile) fd.append("logo", logoFile);

    setSaving(true);
    try {
      if (editingId) {
        await adminApi.put(`/admin/links/${editingId}`, fd);
        showToast("success", "Link updated.");
      } else {
        await adminApi.post("/admin/links", fd);
        showToast("success", "Link added.");
      }
      setShowModal(false);
      fetchLinks();
    } catch (err) {
      setFormError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const allSelected = links.length > 0 && selected.length === links.length;

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar — stays fixed, never scrolls */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 mb-2">
        <div>
          <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
            Manage Links
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <AnimatePresence>
            {selected.length > 0 && (
              <motion.button
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex items-center gap-1.5 bg-red-50 text-red-600 hover:bg-red-100 text-[13px] font-medium px-3 py-1.5 rounded-lg transition disabled:opacity-60"
              >
                {bulkDeleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                Delete Selected ({selected.length})
              </motion.button>
            )}
          </AnimatePresence>
          <button
            onClick={openAddModal}
            className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
          >
            <Plus size={15} />
            Add New Link
          </button>
        </div>
      </div>

      {/* Table card — this is the only part that scrolls, with a sticky header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading links...
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-500">{error}</div>
        ) : links.length === 0 ? (
          <div className="py-16 text-center">
            <Link2 size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">No links yet — click "Add New Link" to create one.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 shadow-md">
                <tr className="text-left text-base">
                  <th className="th-colorful pl-3 pr-2 py-3 w-8 border-b-2 border-r normal-case">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                    />
                  </th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r" style={{textAlign:"center"}}>Logo</th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r">Link Name</th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r">Website URL</th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide text-center border-b-2 border-r">Sequence</th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide text-center border-b-2">Status</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {links.map((link, i) => (
                    <motion.tr
                      key={link.linkid}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.02 }}
                      onClick={() => openEditModal(link)}
                      className={`cursor-pointer hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200 ${
                        selected.includes(link.linkid) ? "bg-[#0EA5A4]/15" : ""
                      }`}
                    >
                      <td className="pl-3 pr-2 py-2 border-r border-slate-200" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.includes(link.linkid)}
                          onChange={() => toggleSelectOne(link.linkid)}
                          className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2 border-r border-slate-200" style={{textAlign:"center"}}>
                        {link.link_logo ? (
                      
                          <a href={logoUrl(link.link_logo)} target="_blank" rel="noopener noreferrer">
                          
                             <div style={{border:"0.5px solid #999",padding:"5px",marginTop:"3px",marginBottom:"3px",borderRadius:"7px"}}><i style={{color:"green"}} class="fa fa-eye" aria-hidden="true"></i></div>
      </a>
                        ) : (
                          <div className="w-10 h-10 rounded-lg border border-dashed border-slate-200 flex items-center justify-center text-slate-300">
                            <ImageOff size={16} />
                          </div>
                        )}



                      </td>
                      <td className="px-3 py-2 text-slate-800 font-medium border-r border-slate-200">{link.link_name}</td>
                      <td className="px-3 py-2 border-r border-slate-200">
                        <a
                          href={link.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-[#0EA5A4] hover:underline max-w-[220px] truncate"
                          title={link.link_url}
                        >
                          <span className="truncate">{link.link_url}</span>
                          <ExternalLink size={12} className="shrink-0" />
                        </a>
                      </td>
                      <td className="px-3 py-2 text-center border-r border-slate-200">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-slate-100 text-slate-600 text-xs font-semibold">
                          {link.seq}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center">
                          <motion.button
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => handleToggleStatus(link, e)}
                            title={link.status ? "Active — click to deactivate" : "Inactive — click to activate"}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition ${
                              link.status
                                ? "bg-[#0EA5A4]/10 text-[#0B7E7D] hover:bg-[#0EA5A4]/20"
                                : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                            }`}
                          >
                            {link.status ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                            {link.status ? "Active" : "Inactive"}
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add / Edit modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h4 className="font-display font-semibold text-[#0B2545]">
                  {editingId ? "Edit Link" : "Add New Link"}
                </h4>
                <button onClick={closeModal} className="text-slate-400 hover:text-slate-700">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                <div>
                 <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                  Link Name <span className="text-red-500">*</span>
                </label>
                  <input
                    type="text"
                    value={form.link_name}
                    onChange={(e) => setForm({ ...form, link_name: e.target.value })}
                    placeholder="e.g. National Medical Commission"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Website URL <span className="text-red-500">*</span>
                  </label>                  
                  <input
                    type="url"
                    value={form.link_url}
                    onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                    placeholder="https://example.gov.in"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Sequence <span className="text-red-500">*</span>
                  </label>
                  {/* <select
                    value={form.seq}
                    onChange={(e) => setForm({ ...form, seq: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition bg-white"
                  >
                    {SEQUENCE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select> */}
                  <input
                    type="number"
                    min="1"
                    value={form.seq}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        seq: e.target.value,
                      })
                    }
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  />
                </div>

                {/* <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Logo (JPG, JPEG, PNG)</label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:border-[#0EA5A4] transition"
                  >
                    <div className="w-12 h-12 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                      {logoPreview ? (
                        <img src={logoPreview} alt="preview" className="w-full h-full object-cover" />
                      ) : existingLogo ? (
                        <img src={logoUrl(existingLogo)} alt="current" className="w-full h-full object-cover" />
                      ) : (
                        <Upload size={16} className="text-slate-300" />
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {logoFile ? logoFile.name : existingLogo ? "Click to replace logo" : "Click to choose a logo file"}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                </div> */}

                <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">
              Logo <span className="text-red-500">*</span>
            </label>

  <div
    onClick={() => fileInputRef.current?.click()}
    className="flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:border-[#0EA5A4] transition"
  >
    <div className="w-12 h-12 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
      {logoPreview ? (
        <img
          src={logoPreview}
          alt="preview"
          className="w-full h-full object-cover"
        />
      ) : existingLogo ? (
        <img
          src={logoUrl(existingLogo)}
          alt="current"
          className="w-full h-full object-cover"
        />
      ) : (
        <Upload size={16} className="text-slate-300" />
      )}
    </div>

    <div className="text-xs text-slate-500">
      {logoFile
        ? logoFile.name
        : existingLogo
          ? "Click to replace logo"
          : "Click to choose a logo file"}
    </div>

    <input
      ref={fileInputRef}
      type="file"
      accept=".jpg,.jpeg,.png,image/jpeg,image/png"
      onChange={handleFileChange}
      className="hidden"
    />
  </div>

  {/* Logo Notes */}
  <div className="mt-2 text-[12px] text-red-600 leading-5">
    <div className="font-semibold">Note: *</div>
    <div>1) Image should be JPEG, JPG or PNG format and less than 2MB.</div>
    <div>2) Image Width = 91px And Height = 90px.</div>

    <div className="font-semibold mt-1">Note: *</div>
    <div>
      Website URL will be like this i.e. : http://www.orisysinfotech.com
    </div>
  </div>
</div>


                {formError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    {formError}
                  </p>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg py-2.5 transition disabled:opacity-60"
                  >
                    {saving ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : editingId ? (
                      <Pencil size={15} />
                    ) : (
                      <Plus size={15} />
                    )}
                    {saving ? "Saving..." : editingId ? "Update Link" : "Submit"}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
